import React, { useRef, useState } from "react";
import ReactCrop from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import { uploadAvatar, getUser } from "../../api";
import {
  ProfileCropWrap,
  ProfileDropzone,
  ProfileGhostBtn,
  ProfileHiddenFile,
  ProfileModal,
  ProfileModalActions,
  ProfileModalDesc,
  ProfileModalOverlay,
  ProfileModalTitle,
  ProfilePrimaryBtn,
} from "./Profile.styles";

const UploadIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    width="24"
    height="24"
  >
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
    <polyline points="17 8 12 3 7 8" />
    <line x1="12" x2="12" y1="3" y2="15" />
  </svg>
);

/**
 * Prod avatar upload + ReactCrop; Lovable modal chrome.
 */
const AvatarModal = ({ open, onClose, userId }) => {
  const fileInputRef = useRef(null);
  const imageRef = useRef(null);
  const [src, setSrc] = useState(null);
  const [crop, setCrop] = useState(undefined);
  const [croppedImageUrl, setCroppedImageUrl] = useState(null);
  const [saving, setSaving] = useState(false);

  if (!open) return null;

  const reset = () => {
    setSrc(null);
    setCrop(undefined);
    setCroppedImageUrl(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSelectFile = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const reader = new FileReader();
    reader.addEventListener("load", () => {
      setSrc(reader.result);
      setCrop({ unit: "%", width: 80, aspect: 1 });
    });
    reader.readAsDataURL(file);
  };

  const getCroppedImg = (image, cropArea) => {
    const canvas = document.createElement("canvas");
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = cropArea.width;
    canvas.height = cropArea.height;
    const ctx = canvas.getContext("2d");
    ctx.drawImage(
      image,
      cropArea.x * scaleX,
      cropArea.y * scaleY,
      cropArea.width * scaleX,
      cropArea.height * scaleY,
      0,
      0,
      cropArea.width,
      cropArea.height
    );
    return canvas.toDataURL("image/jpeg");
  };

  const onCropComplete = async (nextCrop) => {
    if (imageRef.current && nextCrop.width && nextCrop.height) {
      setCroppedImageUrl(getCroppedImg(imageRef.current, nextCrop));
    }
  };

  const confirmImage = async () => {
    const dataUrl = croppedImageUrl || src;
    if (!dataUrl || !userId) return;
    setSaving(true);
    try {
      const data = {
        cropProperties: { x: 0, y: 0, width: 80, height: 80 },
        original: dataUrl,
        medium: dataUrl,
        small: dataUrl,
      };
      const res = await uploadAvatar(userId, data);
      if (res.status === 200) {
        const userRes = await getUser(userId);
        if (userRes.status === 200) {
          userRes.data.avatar.small =
            userRes.data.avatar.small + new Date().getTime();
          localStorage.setItem("user", JSON.stringify(userRes.data));
          window.location.reload();
          return;
        }
      }
    } finally {
      setSaving(false);
    }
    handleClose();
  };

  return (
    <ProfileModalOverlay
      role="presentation"
      onClick={(e) => {
        if (e.target === e.currentTarget) handleClose();
      }}
    >
      <ProfileModal
        role="dialog"
        aria-modal="true"
        aria-labelledby="profile-avatar-title"
        onClick={(e) => e.stopPropagation()}
      >
        <ProfileModalTitle id="profile-avatar-title">
          Change profile photo
        </ProfileModalTitle>
        <ProfileModalDesc>
          Upload an image and crop it to update your profile photo.
        </ProfileModalDesc>

        {!src ? (
          <ProfileDropzone
            type="button"
            onClick={() => fileInputRef.current?.click()}
          >
            <span
              style={{
                display: "flex",
                width: 48,
                height: 48,
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 12,
                background: "hsl(var(--hp-primary) / 0.15)",
                color: "hsl(var(--hp-primary))",
              }}
            >
              <UploadIcon />
            </span>
            <span style={{ fontWeight: 600, fontSize: "0.875rem" }}>
              Choose an image
            </span>
            <span style={{ fontSize: "0.75rem", opacity: 0.7 }}>
              JPG or PNG · click to browse
            </span>
          </ProfileDropzone>
        ) : (
          <ProfileCropWrap>
            <ReactCrop
              src={src}
              crop={crop}
              aspect={1}
              ruleOfThirds
              onImageLoaded={(img) => {
                imageRef.current = img;
              }}
              onComplete={onCropComplete}
              onChange={(next) => setCrop(next)}
            />
          </ProfileCropWrap>
        )}

        <ProfileModalActions>
          <ProfileGhostBtn type="button" onClick={handleClose}>
            Cancel
          </ProfileGhostBtn>
          {src ? (
            <>
              <ProfileGhostBtn
                type="button"
                onClick={() => {
                  reset();
                  fileInputRef.current?.click();
                }}
              >
                Choose another
              </ProfileGhostBtn>
              <ProfilePrimaryBtn
                type="button"
                onClick={confirmImage}
                disabled={saving}
              >
                {saving ? "Saving…" : "Save photo"}
              </ProfilePrimaryBtn>
            </>
          ) : null}
        </ProfileModalActions>

        <ProfileHiddenFile
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={onSelectFile}
        />
      </ProfileModal>
    </ProfileModalOverlay>
  );
};

export default AvatarModal;
